import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Education.css';
import bigIDImage from '../assets/BigID-App-Dev-Pro-Course.png';

// HubSpot Academy Logo
const HubSpotLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none">
    <circle cx="50" cy="50" r="45" fill="#FF7A59"/>
    <circle cx="50" cy="35" r="12" fill="white"/>
    <rect x="45" y="45" width="10" height="20" rx="2" fill="white"/>
    <circle cx="50" cy="75" r="8" fill="white"/>
    <circle cx="75" cy="50" r="8" fill="white"/>
    <rect x="55" y="48" width="15" height="4" rx="2" fill="white"/>
  </svg>
);

const Education = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const education = [
    {
      school: 'Carnegie Mellon University',
      degree: 'Master of Science',
      field: 'Software Management',
      period: '2019 – 2021',
      gpa: '3.8',
      description: 'Combines Business and Computer Science courses into one major. Recommended for software entrepreneurs and product managers.',
      highlights: [
        'Software Product Strategy',
        'Requirements Analysis',
        'Process and Project Management',
        'Data Analytics',
        'Deep Learning and Neural Networks',
      ],
    },
    {
      school: 'Purdue University',
      degree: 'Bachelor of Science',
      field: 'Computer Science, AI/ML Track',
      period: '2014 – 2018',
      gpa: null,
      highlights: [
        'Data Mining and Machine Learning (CS 37300)',
        'Introduction to Artificial Intelligence (CS 47100)',
        'Introduction to the Analysis of Algorithms (CS 38100)',
        'Introduction to Entrepreneurship and Innovation (ENTR 20000)',
        'Marketing & Management for New Ventures (ENTR 31000)',
      ],
      activities: [
        'Founded a B2B startup (Visorware) improving Augmented Reality and Automotive HUD technology',
        'Founded Window\'s Development Club',
        'Participated in several hackathons and innovation competitions',
      ],
      research: [
        {
          title: 'Privacy in Public Databases',
          advisor: 'Prof. Christopher W. Clifton',
          period: 'Sep 2017 – Mar 2019',
          description: 'Research on combining Differential Privacy and k-Anonymity for hierarchical databases (US Census Bureau). Supported by NSF Research Grant.',
        },
        {
          title: 'Finite Difference Regression',
          advisor: 'Prof. Emeritus B.J. Lucier',
          period: 'Oct 2016 – Feb 2018',
          description: 'Novel polynomial regression method published in Open Journal of Statistics.',
        },
        {
          title: 'Lean Scrum for Entrepreneurs',
          advisor: 'Prof. B. Dunsmore',
          period: 'Sep 2017 – Dec 2017',
          description: 'Presented at 2018 Purdue Undergraduate Research Conference.',
        },
      ],
    },
    {
      school: 'Mission San Jose High School',
      degree: 'High School Diploma',
      field: null,
      period: '2010 – 2014',
      gpa: null,
      highlights: [
        'AP Scholar with Honor Award',
        'Intel Science Talent Search Research Report Badge',
      ],
      activities: [
        'J.V. Badminton',
      ],
    },
  ];

  const languages = [
    { name: 'English', level: 'Native or bilingual proficiency', percent: 100 },
    { name: 'Bengali', level: 'Professional working proficiency', percent: 80 },
    { name: 'Spanish', level: 'Professional working proficiency', percent: 75 },
    { name: 'Hindi', level: 'Elementary proficiency', percent: 40 },
  ];

  const certifications = [
    {
      name: 'HubSpot Sales Hub Software',
      issuer: 'HubSpot Academy',
      date: 'Dec 2024',
      LogoComponent: HubSpotLogo,
    },
    {
      name: 'BigID AppDev Pro Course',
      issuer: 'BigID',
      date: 'Mar 2021',
      image: bigIDImage,
    },
  ];

  const awards = [
    {
      title: 'Intel STS Research Report Badge Recipient',
      issuer: 'Intel Science Talent Search',
      year: '2014',
      description: 'One of 264 students nationwide for creating a Novel Statistical Regression method based on Finite Differences and T-tests.',
    },
    {
      title: 'AP Scholar with Honor Award',
      issuer: 'College Board',
      year: '2014',
      description: 'Outstanding performance in AP Examinations.',
    },
  ];

  return (
    <section id="education" className="education section" ref={ref}>
      <div className="education-background">
        <div className="edu-pattern"></div>
      </div>
      
      <div className="container">
        <motion.div
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>Education</h2>
          <p className="section-subtitle">
            Elite institutions with focus on AI/ML and Product Management
          </p>
        </motion.div>

        <div className="education-grid">
          {education.map((edu, index) => (
            <motion.div
              key={index}
              className="education-card"
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <div className="edu-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                  <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                </svg>
              </div>
              
              <div className="edu-content">
                <h3 className="edu-school">{edu.school}</h3>
                <div className="edu-degree">
                  <span className="degree-type">{edu.degree}</span>
                  {edu.field && <span className="degree-field">{edu.field}</span>}
                </div>
                <div className="edu-meta">
                  <span className="edu-period">{edu.period}</span>
                  {edu.gpa && <span className="edu-gpa">GPA: {edu.gpa}</span>}
                </div>

                {edu.description && (
                  <p className="edu-description">{edu.description}</p>
                )}

                {edu.highlights && edu.highlights.length > 0 && (
                  <div className="edu-highlights">
                    <h4>{edu.research ? 'Coursework' : 'Highlights'}</h4>
                    <ul>
                      {edu.highlights.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {edu.activities && (
                  <div className="edu-activities">
                    <h4>Activities & Leadership</h4>
                    <ul>
                      {edu.activities.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {edu.research && (
                  <div className="edu-research">
                    <h4>Research Experience</h4>
                    <div className="research-list">
                      {edu.research.map((item, i) => (
                        <div key={i} className="research-item">
                          <span className="research-title">{item.title}</span>
                          <span className="research-advisor">{item.advisor} • {item.period}</span>
                          <span className="research-desc">{item.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Certifications */}
        <motion.div
          className="certifications"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3>Licenses & Certifications</h3>
          <div className="cert-grid">
            {certifications.map((cert, index) => (
              <div key={index} className="cert-card">
                {cert.image ? (
                  <img src={cert.image} alt={cert.name} className="cert-image" />
                ) : cert.LogoComponent ? (
                  <cert.LogoComponent className="cert-logo-svg" />
                ) : (
                  <span className="cert-icon">{cert.icon}</span>
                )}
                <div className="cert-info">
                  <span className="cert-name">{cert.name}</span>
                  <span className="cert-issuer">{cert.issuer} • {cert.date}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Awards */}
        <motion.div
          className="awards-section"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h3>Honors & Awards</h3>
          <div className="awards-grid">
            {awards.map((award, index) => (
              <div key={index} className="award-card">
                <div className="award-icon">🏆</div>
                <div className="award-content">
                  <h4>{award.title}</h4>
                  <span className="award-issuer">{award.issuer} • {award.year}</span>
                  <p>{award.description}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Languages */}
        <motion.div
          className="languages-section"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h3>Languages</h3>
          <div className="languages-grid">
            {languages.map((lang, index) => (
              <div key={index} className="language-card">
                <div className="language-header">
                  <span className="language-name">{lang.name}</span>
                  <span className="language-level">{lang.level}</span>
                </div>
                <div className="language-bar">
                  <motion.div 
                    className="language-progress"
                    initial={{ width: 0 }}
                    animate={inView ? { width: `${lang.percent}%` } : {}}
                    transition={{ duration: 1, delay: 0.7 + index * 0.1 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Education;

